/**
 * Topics · Konu Evreni
 *
 * Drill-down browser. URL state:
 *  /topics                                → ExamClassification listesi
 *  /topics/:examId                        → Branş listesi
 *  /topics/:examId/:branchId              → Root topic listesi
 *  /topics/:examId/:branchId/:topicId     → Alt topic listesi VEYA lecture'lar
 *
 * Erişimi olmayan kart üzerinde ⊘ overlay + "Aboneliğin yok" pill.
 * AppShell BackButton (useTelegramBackButton) navigation'ı yönetir.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  HStack,
  Heading,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  api,
  type BranchItem,
  type ExamClassificationItem,
  type LectureMetaItem,
  type TopicItem,
} from '../lib/api';
import { haptic } from '../telegram';
import { BRAND, ICON, type Tier } from '@algobot/shared';
import PlanBadge from '../components/PlanBadge';

type Level = 'exams' | 'branches' | 'topics' | 'leaf';

interface BreadcrumbEntry {
  label: string;
  to: string;
}

export default function TopicsPage() {
  const params = useParams<{ examId?: string; branchId?: string; topicId?: string }>();
  const nav = useNavigate();

  const examId = params.examId ? Number(params.examId) : null;
  const branchId = params.branchId ? Number(params.branchId) : null;
  const topicId = params.topicId ? Number(params.topicId) : null;

  const level: Level = topicId
    ? 'leaf'
    : branchId
      ? 'topics'
      : examId
        ? 'branches'
        : 'exams';

  // Resolved metadata for breadcrumbs
  const [exam, setExam] = useState<ExamClassificationItem | null>(null);
  const [branch, setBranch] = useState<BranchItem | null>(null);
  const [topic, setTopic] = useState<TopicItem | null>(null);

  // Each level has its own data
  const [exams, setExams] = useState<ExamClassificationItem[]>([]);
  const [branches, setBranches] = useState<BranchItem[]>([]);
  const [topics, setTopics] = useState<TopicItem[]>([]);
  const [subTopics, setSubTopics] = useState<TopicItem[]>([]);
  const [lectures, setLectures] = useState<LectureMetaItem[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Veriyi seviyeye göre yükle ─────────────────────────
  useEffect(() => {
    let canceled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        if (level === 'exams') {
          const data = await api.examClassifications();
          if (!canceled) {
            setExams(data);
            setExam(null); setBranch(null); setTopic(null);
          }
          return;
        }

        if (level === 'branches' && examId) {
          const [examList, branchList] = await Promise.all([
            api.examClassifications(),
            api.branches(examId),
          ]);
          if (canceled) return;
          const ex = examList.find((e) => e.id === examId) || null;
          setExam(ex);
          setBranches(branchList);
          setBranch(null); setTopic(null);
          return;
        }

        if (level === 'topics' && examId && branchId) {
          const [examList, branchList, topicList] = await Promise.all([
            api.examClassifications(),
            api.branches(examId),
            api.topicsList({ branchId, examClassificationId: examId, parentId: null }),
          ]);
          if (canceled) return;
          setExam(examList.find((e) => e.id === examId) || null);
          setBranch(branchList.find((b) => b.id === branchId) || null);
          setTopics(topicList);
          setTopic(null);
          return;
        }

        if (level === 'leaf' && examId && branchId && topicId) {
          // Root topics ve children'ı çek; current topic ve sub topic'leri belirle
          const [examList, branchList, allTopicsForBranch, childrenList, lectureList] = await Promise.all([
            api.examClassifications(),
            api.branches(examId),
            api.topicsList({ branchId, examClassificationId: examId }),
            api.topicsList({ parentId: topicId }),
            api.lecturesByTopic(topicId).catch(() => [] as LectureMetaItem[]),
          ]);
          if (canceled) return;
          setExam(examList.find((e) => e.id === examId) || null);
          setBranch(branchList.find((b) => b.id === branchId) || null);
          const cur = allTopicsForBranch.find((t) => t.id === topicId) || null;
          setTopic(cur);
          setSubTopics(childrenList);
          setLectures(lectureList);
        }
      } catch (err: any) {
        if (canceled) return;
        if (err?.status === 401) setError('Telegram içinde aç.');
        else setError('Konu verisi alınamadı.');
      } finally {
        if (!canceled) setLoading(false);
      }
    };

    run();
    return () => { canceled = true; };
  }, [level, examId, branchId, topicId]);

  // ── Breadcrumbs ────────────────────────────────────────
  const crumbs: BreadcrumbEntry[] = useMemo(() => {
    const out: BreadcrumbEntry[] = [{ label: BRAND.menuLabels.topicUniverse, to: '/topics' }];
    if (exam) out.push({ label: exam.name, to: `/topics/${exam.id}` });
    if (exam && branch) out.push({ label: branch.name, to: `/topics/${exam.id}/${branch.id}` });
    if (exam && branch && topic) out.push({ label: topic.name, to: `/topics/${exam.id}/${branch.id}/${topic.id}` });
    return out;
  }, [exam, branch, topic]);

  return (
    <Container maxW="md" py={3} px={3}>
      <VStack align="stretch" spacing={3}>
        <HStack spacing={2} align="center">
          <Text fontSize="xl" lineHeight="1" color="accent" fontWeight="bold">
            {BRAND.emojiContent.lecture}
          </Text>
          <Heading size="md" color="textPrimary">
            {BRAND.menuLabels.topicUniverse}
          </Heading>
        </HStack>

        {/* Breadcrumb */}
        {crumbs.length > 1 ? (
          <Box
            bg="bgSecondary"
            borderWidth="1px"
            borderColor="borderPrimary"
            borderRadius="md"
            px={2.5}
            py={1.5}
          >
            <HStack spacing={1} flexWrap="wrap" rowGap={1}>
              {crumbs.map((c, i) => {
                const isLast = i === crumbs.length - 1;
                return (
                  <HStack key={c.to} spacing={1}>
                    {i > 0 ? (
                      <Text fontSize="11px" color="textMuted">{ICON.forward}</Text>
                    ) : null}
                    {isLast ? (
                      <Text
                        fontSize="11px"
                        color="textPrimary"
                        fontWeight="semibold"
                        noOfLines={1}
                        maxW="200px"
                      >
                        {c.label}
                      </Text>
                    ) : (
                      <Box
                        as={RouterLink}
                        to={c.to}
                        fontSize="11px"
                        color="accent"
                        noOfLines={1}
                        maxW="160px"
                        _hover={{ textDecoration: 'underline' }}
                      >
                        {c.label}
                      </Box>
                    )}
                  </HStack>
                );
              })}
            </HStack>
          </Box>
        ) : null}

        {loading ? (
          <VStack p={6} spacing={2}>
            <Spinner color="accent" />
            <Text fontSize="sm" color="textSecondary">Yükleniyor…</Text>
          </VStack>
        ) : error ? (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            {error}
          </Alert>
        ) : (
          <>
            {level === 'exams' && (
              <ExamGrid
                exams={exams}
                onPick={(e) => {
                  haptic('select');
                  nav(`/topics/${e.id}`);
                }}
              />
            )}
            {level === 'branches' && (
              <BranchList
                branches={branches}
                onPick={(b) => {
                  haptic('select');
                  nav(`/topics/${examId}/${b.id}`);
                }}
              />
            )}
            {level === 'topics' && (
              <TopicList
                topics={topics}
                onPick={(t) => {
                  haptic('select');
                  nav(`/topics/${examId}/${branchId}/${t.id}`);
                }}
              />
            )}
            {level === 'leaf' && (
              <LeafView
                subTopics={subTopics}
                lectures={lectures}
                onPickTopic={(t) => {
                  haptic('select');
                  nav(`/topics/${examId}/${branchId}/${t.id}`);
                }}
                onPickLecture={(l) => {
                  haptic('select');
                  nav(`/lecture/${l.id}`);
                }}
              />
            )}
          </>
        )}

        <Button variant="ghost" onClick={() => nav('/')}>
          {BRAND.menuLabels.home}
        </Button>
      </VStack>
    </Container>
  );
}

// ──────────────────────────────────────────────────────────
// Level views
// ──────────────────────────────────────────────────────────

function ExamGrid({
  exams,
  onPick,
}: {
  exams: ExamClassificationItem[];
  onPick: (e: ExamClassificationItem) => void;
}) {
  if (exams.length === 0) {
    return <EmptyState text="Şu anda görünür bir sınav sınıflandırması yok." />;
  }
  return (
    <SimpleGrid columns={1} spacing={2}>
      {exams.map((e) => (
        <NodeCard
          key={e.id}
          title={e.name}
          subtitle="Sınav sınıflandırması"
          tier={e.tier as Tier}
          accessible={e.accessible !== false}
          onClick={() => onPick(e)}
        />
      ))}
    </SimpleGrid>
  );
}

function BranchList({
  branches,
  onPick,
}: {
  branches: BranchItem[];
  onPick: (b: BranchItem) => void;
}) {
  if (branches.length === 0) {
    return <EmptyState text="Bu sınavda görünür branş bulunamadı." />;
  }
  return (
    <SimpleGrid columns={1} spacing={2}>
      {branches.map((b) => (
        <NodeCard
          key={b.id}
          title={b.name}
          subtitle="Branş"
          accessible={b.accessible !== false}
          onClick={() => onPick(b)}
        />
      ))}
    </SimpleGrid>
  );
}

function TopicList({
  topics,
  onPick,
}: {
  topics: TopicItem[];
  onPick: (t: TopicItem) => void;
}) {
  if (topics.length === 0) {
    return <EmptyState text="Bu branşta kök konu bulunamadı." />;
  }
  return (
    <SimpleGrid columns={1} spacing={2}>
      {topics.map((t) => (
        <NodeCard
          key={t.id}
          title={t.name}
          subtitle="Konu"
          accessible={t.accessible !== false}
          onClick={() => onPick(t)}
        />
      ))}
    </SimpleGrid>
  );
}

function LeafView({
  subTopics,
  lectures,
  onPickTopic,
  onPickLecture,
}: {
  subTopics: TopicItem[];
  lectures: LectureMetaItem[];
  onPickTopic: (t: TopicItem) => void;
  onPickLecture: (l: LectureMetaItem) => void;
}) {
  // Çocuğu varsa alt konu listesi; yoksa lecture'ları göster.
  if (subTopics.length > 0) {
    return (
      <VStack align="stretch" spacing={3}>
        <SectionLabel text="Alt Konular" />
        <SimpleGrid columns={1} spacing={2}>
          {subTopics.map((t) => (
            <NodeCard
              key={t.id}
              title={t.name}
              subtitle="Alt konu"
              accessible={t.accessible !== false}
              onClick={() => onPickTopic(t)}
            />
          ))}
        </SimpleGrid>
        {lectures.length > 0 ? (
          <>
            <SectionLabel text="Bu Konudaki Lecture'lar" />
            <LectureList lectures={lectures} onPick={onPickLecture} />
          </>
        ) : null}
      </VStack>
    );
  }
  if (lectures.length === 0) {
    return <EmptyState text="Bu konu altında alt konu veya konu anlatımı bulunamadı." />;
  }
  return (
    <VStack align="stretch" spacing={2}>
      <SectionLabel text="Konu Anlatımları" />
      <LectureList lectures={lectures} onPick={onPickLecture} />
    </VStack>
  );
}

function LectureList({ lectures, onPick }: { lectures: LectureMetaItem[]; onPick: (l: LectureMetaItem) => void }) {
  return (
    <SimpleGrid columns={1} spacing={2}>
      {lectures.map((l) => (
        <Box
          key={l.id}
          as="button"
          type="button"
          textAlign="left"
          bg="bgCard"
          borderWidth="1px"
          borderColor="borderPrimary"
          borderRadius="lg"
          boxShadow="cardElevated"
          px={3}
          py={2.5}
          _hover={{ borderColor: 'accent' }}
          onClick={() => onPick(l)}
        >
          <HStack spacing={2.5} align="center">
            <Box
              w="36px"
              h="36px"
              borderRadius="md"
              bg="bgSecondary"
              borderWidth="1px"
              borderColor="borderSecondary"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <Text fontSize="md" lineHeight="1" color="accent" fontWeight="bold">
                {ICON.lecture}
              </Text>
            </Box>
            <Text flex="1" fontSize="sm" fontWeight="semibold" color="textPrimary" noOfLines={2}>
              {l.title}
            </Text>
            <Text color="textMuted">{ICON.forward}</Text>
          </HStack>
        </Box>
      ))}
    </SimpleGrid>
  );
}

// ──────────────────────────────────────────────────────────
// Building blocks
// ──────────────────────────────────────────────────────────

function NodeCard({
  title,
  subtitle,
  tier,
  accessible,
  onClick,
}: {
  title: string;
  subtitle?: string;
  tier?: Tier;
  accessible: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      as="button"
      type="button"
      onClick={accessible ? onClick : undefined}
      textAlign="left"
      bg="bgCard"
      borderWidth="1px"
      borderColor={accessible ? 'borderPrimary' : 'borderSecondary'}
      borderRadius="xl"
      boxShadow="cardElevated"
      px={3.5}
      py={3}
      position="relative"
      overflow="hidden"
      cursor={accessible ? 'pointer' : 'not-allowed'}
      opacity={accessible ? 1 : 0.7}
      _hover={accessible ? { borderColor: 'accent', transform: 'translateY(-1px)' } : {}}
      transition="transform 0.18s ease, border-color 0.18s ease"
    >
      <HStack spacing={2.5} align="center">
        <Box flex="1" minW={0}>
          <HStack spacing={1.5} mb={0.5}>
            {subtitle ? (
              <Text
                fontSize="10px"
                color="textMuted"
                fontWeight="semibold"
                textTransform="uppercase"
                letterSpacing="0.05em"
                noOfLines={1}
              >
                {subtitle}
              </Text>
            ) : null}
            {tier ? <PlanBadge tier={tier} /> : null}
          </HStack>
          <Text fontSize="sm" fontWeight="bold" color="textPrimary" noOfLines={2}>
            {title}
          </Text>
          {!accessible ? (
            <HStack spacing={1} mt={1}>
              <Text fontSize="md" lineHeight="1" color="textMuted">{ICON.lock}</Text>
              <Badge colorScheme="gray" textTransform="none" fontSize="10px">
                Aboneliğin yok
              </Badge>
            </HStack>
          ) : null}
        </Box>
        <Text color={accessible ? 'accent' : 'textMuted'} fontSize="lg">
          {accessible ? ICON.forward : ICON.lock}
        </Text>
      </HStack>
    </Box>
  );
}

function SectionLabel({ text }: { text: string }) {
  return (
    <Text
      fontSize="10px"
      color="textMuted"
      fontWeight="semibold"
      textTransform="uppercase"
      letterSpacing="0.05em"
    >
      {text}
    </Text>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <Box
      bg="bgSecondary"
      borderWidth="1px"
      borderStyle="dashed"
      borderColor="borderPrimary"
      borderRadius="md"
      px={3}
      py={4}
      textAlign="center"
    >
      <Text fontSize="sm" color="textSecondary">{text}</Text>
    </Box>
  );
}
